import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (isbn: string) => void;
}

export function BarcodeScanner({ open, onClose, onScan }: BarcodeScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const readerElementId = 'barcode-reader';

  useEffect(() => {
    if (open) {
      startScanner();
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [open]);

  const startScanner = async () => {
    try {
      setError(null);
      setScanning(true);

      const scanner = new Html5Qrcode(readerElementId);
      scannerRef.current = scanner;

      // Request camera permissions and start scanning
      await scanner.start(
        { facingMode: 'environment' }, // Use rear camera on mobile
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
        },
        (decodedText) => {
          // Successfully scanned a barcode
          const cleaned = decodedText.replace(/[-\s]/g, '');
          
          // Validate it's an ISBN (10 or 13 digits)
          if (/^\d{10}$/.test(cleaned) || /^\d{13}$/.test(cleaned)) {
            onScan(cleaned);
            stopScanner();
            onClose();
          } else {
            setError('Invalid barcode. Please scan an ISBN barcode (10 or 13 digits).');
          }
        },
        (errorMessage) => {
          // Scanning in progress, no barcode detected yet
          // Don't show these errors to avoid UI noise
        }
      );
    } catch (err: any) {
      console.error('Scanner error:', err);
      if (err?.name === 'NotAllowedError' || err?.message?.includes('Permission')) {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (err?.name === 'NotFoundError') {
        setError('No camera found. Please connect a camera or use manual ISBN entry.');
      } else {
        setError('Failed to start camera. Please try again or use manual ISBN entry.');
      }
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current?.isScanning) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Scan Book Barcode
          </DialogTitle>
          <DialogDescription>
            Point your camera at the barcode on the back of your book
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Camera preview container */}
          <div 
            id={readerElementId} 
            className="w-full rounded-lg overflow-hidden bg-muted"
            style={{ minHeight: '300px' }}
          />

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            {error && (
              <Button
                onClick={startScanner}
                className="flex-1"
              >
                Try Again
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
